import { useMemo } from 'react'

import { EMPTY_OBJECT } from '../constants/common'

import { useApiAbortController } from './useApiAbortController'

/**
 * The {@link ApiCall} type describes a function to invoke {@link CallApi}.
 *
 * @typedef {(callApi: CallApi<TResponse>, apiConfig?: ApiConfig) => CallApiReturn<TResponse>} ApiCall
 * @template TResponse
 */

/**
 * The {@link ApiAbort} type describes a function to abort currently
 *    invoked {@link CallApi}.
 *
 * @typedef {() => void} ApiAbort
 */

/**
 * The {@link Api} type describes return type of the {@link useApi} hook
 *
 * @typedef {object} Api
 * @template TResponse
 *
 * @property {ApiCall<TResponse>} call
 * @property {ApiAbort} abort
 */

/**
 * @template TResponse
 *
 * @param {CallApi<TResponse>} callApi
 * @param {Partial<ApiConfig>} apiConfig
 * @param {AbortController} abortController
 *
 * @returns {CallApiReturn}
 */
async function call(callApi, apiConfig, { signal }) {
  try {
    return await callApi({
      ...apiConfig,
      options: { ...apiConfig.options, signal },
    })
  } catch (reason) {
    if (signal.aborted) {
      return null
    }

    throw reason
  }
}

/**
 * @template TResponse
 *
 * The {@link useApi} hook creates functions to call and abort API.
 *
 * @example
 * ```
 *  function SomeComponent({ param }) {
 *    const getDataApi = useApi()
 *
 *    const handleCallClick = useCallback(() => {
 *      getDataApi.call(getData, { params: { param } })
 *    }, [getDataApi, param])
 *
 *    const handleAbortClick = useCallBack(() => {
 *      getDataApi.abort()
 *    }, [getDataApi])
 *
 *    //...
 *  }
 * ```
 *
 * @returns {Api<TResponse>}
 */
export function useApi({ rejectAborted = false } = EMPTY_OBJECT) {
  const abortControllerRef = useApiAbortController(null)

  return useMemo(
    () => ({
      abort() {
        abortControllerRef.current?.abort()
      },
      call(callApi, config = EMPTY_OBJECT) {
        abortControllerRef.current?.abort()

        const abortController = new AbortController()

        abortControllerRef.current = abortController

        return new Promise((resolve, reject) => {
          call(callApi, config, abortController)
            .then(response => {
              if (abortControllerRef.current) {
                if (abortControllerRef.current === abortController) {
                  abortControllerRef.current = null
                }

                resolve(response)
              }
            })
            .catch(reason => {
              if (abortControllerRef.current && (reason || rejectAborted)) {
                reject(reason)
              }
            })
        })
      },
    }),
    [abortControllerRef, rejectAborted]
  )
}
